import { get, writable } from 'svelte/store';
import { format } from 'src/services/i18n';
import { real_id } from 'src/helpers/interface_helpers';
import { PttUiState } from '../types/ptt-ui-state';
import type {
	WebRTCSessionHandle,
	WebRTCSessionLease,
	WebRTCSessionListeners,
	WebRTCStreamError,
} from '../types/webrtc-session';
import { WebRTCClient } from './webrtc';
import {
	CLOUD_RETRY_DELAY_MS,
	DISCONNECTED_RETRY_DELAY_MS,
	SESSION_LINGER_MS,
	SLOT_RETRY_DELAY_MS,
	STREAM_RETRY_DELAY_MS,
	UNLOAD_RESUME_DELAY_MS,
} from './webrtc-config';
import { createTalkController, type TalkConsumer, type TalkController } from './webrtc-talk';
import { webrtcTrace } from './webrtc-trace';
import {
	NO_STREAM_ERROR,
	clearTimer,
	isAwaitingCloudConnection,
	isSlotConsumingError,
	stopMediaStreamTracks,
	webrtcSessionKey,
} from './webrtc-utils';

const _format = get(format);

/**
 * One live camera connection shared by every player showing that camera.
 *
 * Cameras accept only 3 simultaneous streams, so a session is keyed by
 * `deviceId:streamId` and reference counted: the first consumer negotiates it,
 * later consumers attach to the same `MediaStream`, and the connection is torn
 * down `SESSION_LINGER_MS` after the last consumer leaves.
 *
 * `generation` invalidates callbacks belonging to a replaced client. A single
 * fault makes `WebRTCClient` emit several signals (`start_error`, connection
 * `failed`, ICE `disconnected`); without this guard each one would build
 * another connection and multiply the streams this module exists to limit.
 */
interface WebRTCSession {
	key: string;
	deviceId: string;
	streamId: number;
	generation: number;
	client: WebRTCClient | null;
	stream: MediaStream | null;
	loading: boolean;
	error: WebRTCStreamError;
	consumers: Set<WebRTCSessionListeners>;
	closeTimeout: ReturnType<typeof setTimeout> | null;
	retryTimeout: ReturnType<typeof setTimeout> | null;
	talk: TalkController;
}

const sessions = new Map<string, WebRTCSession>();

/**
 * Whether this app already holds a session on `deviceId`, on any stream id.
 *
 * Lets views tell a camera that is full because of someone else apart from one
 * that is full because of us: our own stream is counted in the device's
 * reported `streams`, and blocking on that would hide the very stream being
 * watched. True from the moment the session opens rather than when media
 * arrives, so the window while negotiating is covered, and through the linger,
 * during which the slot is still ours.
 *
 * Reads the session map directly, so it is not reactive on its own. Callers
 * must invoke it inside the same reactive statement as the `streams` value they
 * are testing — that status push is what re-evaluates the result.
 */
export const hasActiveWebRTCSession = (deviceId: string | undefined): boolean => {
	if (!deviceId) {
		return false;
	}
	const id = real_id(deviceId);
	return Array.from(sessions.values()).some((session) => real_id(session.deviceId) === id);
};

let unloadTeardownRegistered = false;

/** Tags a trace line with the session it belongs to and its sharing state. */
const trace = (session: WebRTCSession, event: string, detail?: Record<string, unknown>) => {
	webrtcTrace('Session', `${session.key} · ${event}`, {
		consumers: session.consumers.size,
		gen: session.generation,
		...detail,
	});
};

/**
 * Notifies every consumer. The set is copied first because a consumer may
 * release itself while reacting to the notification.
 */
const broadcast = (session: WebRTCSession, notify: (listener: WebRTCSessionListeners) => void) => {
	Array.from(session.consumers).forEach(notify);
};

const setLoading = (session: WebRTCSession, loading: boolean) => {
	if (session.loading !== loading) {
		trace(session, `loading → ${loading}`);
	}
	session.loading = loading;
	broadcast(session, (listener) => listener.onLoading?.(loading));
};

const setError = (session: WebRTCSession, error: WebRTCStreamError) => {
	if (session.error.heading !== error.heading || session.error.message !== error.message) {
		trace(session, `error → ${error.heading || '(none)'}`, { message: error.message });
	}
	session.error = error;
	broadcast(session, (listener) => listener.onError?.(error));
};

const setStream = (session: WebRTCSession, stream: MediaStream | null) => {
	trace(session, `stream → ${stream ? 'live' : 'null'}`, {
		tracks: stream?.getTracks().map((track) => `${track.kind}:${track.readyState}`),
	});
	session.stream = stream;
	broadcast(session, (listener) => listener.onStream?.(stream));
};

/**
 * Ends the shared `MediaStream`. Only the session may do this — a consumer
 * that stopped these tracks would blank the video for every other player.
 */
const releaseStream = (session: WebRTCSession) => {
	if (!session.stream) {
		return;
	}
	stopMediaStreamTracks(session.stream);
	setStream(session, null);
};

const attachAudioTrack = (session: WebRTCSession, event: RTCTrackEvent) => {
	if (session.stream) {
		if (session.stream.getAudioTracks().length === 0) {
			session.stream.addTrack(event.track);
		}
		return;
	}
	setStream(session, event.streams?.length ? event.streams[0] : new MediaStream([event.track]));
};

const handleTrack = (session: WebRTCSession, event: RTCTrackEvent) => {
	if (event?.track?.kind === 'audio') {
		attachAudioTrack(session, event);
		return;
	}
	if (event?.track?.kind !== 'video') {
		return;
	}
	if (!event?.streams?.length) {
		failSession(
			session,
			{
				heading: _format('camera_live_stream_error', { default: 'Live Stream Error' }),
				message: _format('camera_no_stream', { default: 'No stream to play' }),
			},
			STREAM_RETRY_DELAY_MS,
		);
		return;
	}

	// Media is flowing, so the connection phase is over and any error raised
	// while getting here is stale. Each player still reports its own `<video>`
	// buffering on top of this.
	setError(session, NO_STREAM_ERROR);
	setLoading(session, false);
	setStream(session, event.streams[0]);
};

const handleStartError = (session: WebRTCSession, error: Error | string) => {
	const message = typeof error === 'string' ? error : (error?.message ?? String(error));
	if (isAwaitingCloudConnection(error)) {
		setError(session, NO_STREAM_ERROR);
		scheduleRetry(session, CLOUD_RETRY_DELAY_MS);
		return;
	}
	// A slot the camera has not reclaimed yet is not worth re-requesting a
	// second later: that is what exhausts its three streams.
	failSession(
		session,
		{ heading: _format('camera_stream_error', { default: 'Stream Error' }), message },
		isSlotConsumingError(error) ? SLOT_RETRY_DELAY_MS : CLOUD_RETRY_DELAY_MS,
	);
};

const handleConnectionState = (
	session: WebRTCSession,
	eventName: string,
	peerConnection: RTCPeerConnection,
	event: Event | Error | string,
) => {
	if (eventName === 'start_error') {
		handleStartError(session, event as Error | string);
		return;
	}
	if (eventName === 'onicegatheringstatechange' || !peerConnection) {
		return;
	}

	trace(session, `pc ${eventName}`, {
		connection: peerConnection.connectionState,
		ice: peerConnection.iceConnectionState,
		signaling: peerConnection.signalingState,
	});

	if (peerConnection.iceConnectionState === 'disconnected') {
		trace(session, 'ice disconnected → dropping the connection');
		failSession(
			session,
			{
				heading: _format('camera_stream_disconnected', { default: 'Stream Disconnected' }),
				message: _format('camera_stream_reconnecting', { default: 'Reconnecting...' }),
			},
			DISCONNECTED_RETRY_DELAY_MS,
		);
	}
};

/**
 * Drops the current client and its media. Bumping the generation first means
 * any event still queued for the outgoing client is ignored.
 */
const teardownConnection = (session: WebRTCSession) => {
	trace(session, 'teardown', { hadClient: !!session.client });
	session.generation += 1;
	session.talk.reset();

	const client = session.client;
	session.client = null;
	client?.stop();

	releaseStream(session);
};

const connect = (session: WebRTCSession) => {
	teardownConnection(session);

	const generation = session.generation;
	const isCurrent = () => session.generation === generation;

	trace(session, 'connect');
	setLoading(session, true);
	session.client = new WebRTCClient(
		session.deviceId,
		{
			onTrack: (event) => {
				if (isCurrent()) {
					handleTrack(session, event);
				}
			},
			onConnectionState: (eventName, peerConnection, event) => {
				if (isCurrent()) {
					handleConnectionState(session, eventName, peerConnection, event);
				}
			},
			onTalkState: (signal) => {
				if (isCurrent()) {
					session.talk.signal(signal);
				}
			},
		},
		session.streamId,
	);
};

/**
 * Only ever one retry in flight: a single fault produces a burst of failure
 * signals, and each one must not schedule its own reconnect.
 */
const scheduleRetry = (session: WebRTCSession, delay: number) => {
	if (session.retryTimeout) {
		return;
	}
	trace(session, `retry in ${delay}ms`);
	session.retryTimeout = setTimeout(() => {
		session.retryTimeout = null;
		if (session.consumers.size) {
			connect(session);
		}
	}, delay);
};

const failSession = (session: WebRTCSession, error: WebRTCStreamError, retryDelay: number) => {
	if (session.retryTimeout) {
		return;
	}
	setError(session, error);
	teardownConnection(session);
	setLoading(session, true);
	scheduleRetry(session, retryDelay);
};

/**
 * Synchronous by design: this also runs from `beforeunload`, where the
 * `streamer.stopstream` frame only reaches the socket if no `await` precedes
 * `client.stop()`.
 */
const destroySession = (session: WebRTCSession) => {
	trace(session, 'destroy');
	sessions.delete(session.key);
	session.closeTimeout = clearTimer(session.closeTimeout);
	session.retryTimeout = clearTimer(session.retryTimeout);
	teardownConnection(session);
	session.consumers.clear();
};

export const destroyAllWebRTCSessions = () => {
	Array.from(sessions.values()).forEach(destroySession);
};

/**
 * Frees every camera's stream slot without abandoning the players still using
 * it. Neither `beforeunload` nor `pagehide` proves the page is gone: the user
 * can cancel the navigation, and mobile fires `pagehide` for app switches,
 * screen locks and bfcache. Destroying outright left mounted players holding a
 * handle to a session no longer in the map — talkback pinned to `unavailable`
 * and no way back, since `retry` refuses to revive an unregistered session.
 *
 * So the connection is dropped (which issues `streamer.stopstream`, the part
 * that must happen if the page really is closing) while the session itself is
 * kept and its reconnect scheduled. If the page does go away, the timer goes
 * with it.
 */
const suspendAllSessions = (event: Event) => {
	webrtcTrace('Session', `'${event.type}' → suspending ${sessions.size} session(s)`);
	Array.from(sessions.values()).forEach((session) => {
		if (!session.consumers.size) {
			destroySession(session);
			return;
		}
		session.retryTimeout = clearTimer(session.retryTimeout);
		teardownConnection(session);
		setLoading(session, true);
		scheduleRetry(session, UNLOAD_RESUME_DELAY_MS);
	});
};

/**
 * Reconnects sessions whose players are still mounted, on bfcache restore.
 * Pre-empts the reconnect `suspendAllSessions` scheduled, so a restored page
 * gets its stream back at once instead of waiting the delay out.
 */
const resumeAllSessions = (event: Event) => {
	webrtcTrace('Session', `'${event.type}' → resuming ${sessions.size} session(s)`);
	Array.from(sessions.values()).forEach((session) => {
		if (!session.consumers.size) {
			destroySession(session);
			return;
		}
		if (session.client) {
			return;
		}
		session.retryTimeout = clearTimer(session.retryTimeout);
		connect(session);
	});
};

const registerUnloadTeardown = () => {
	if (unloadTeardownRegistered || typeof window === 'undefined') {
		return;
	}
	unloadTeardownRegistered = true;

	// `pagehide` covers the cases `beforeunload` misses: mobile Safari, bfcache
	// navigations and tab discards. `visibilitychange` is deliberately not used
	// — it fires on ordinary tab switches and would kill live streams.
	window.addEventListener('beforeunload', suspendAllSessions);
	window.addEventListener('pagehide', suspendAllSessions);
	// A restored page keeps its players mounted, so they need the connection
	// back immediately rather than waiting out the scheduled retry.
	window.addEventListener('pageshow', resumeAllSessions);
};

const createSession = (deviceId: string, streamId: number, key: string): WebRTCSession => {
	const session = {
		key,
		deviceId,
		streamId,
		generation: 0,
		client: null,
		stream: null,
		loading: true,
		error: NO_STREAM_ERROR,
		consumers: new Set<WebRTCSessionListeners>(),
		closeTimeout: null,
		retryTimeout: null,
	} as WebRTCSession;
	session.talk = createTalkController({
		mic: () => session.client,
		publish: (state) => broadcast(session, (listener) => listener.onPttState?.(state)),
		trace: (event, detail) => trace(session, event, detail),
	});
	return session;
};

/**
 * Returns the live session for this camera, cancelling a pending teardown so a
 * screen switch reuses the connection, or opens a new one.
 */
const openSession = (deviceId: string, streamId: number): WebRTCSession => {
	const key = webrtcSessionKey(deviceId, streamId);
	const existing = sessions.get(key);

	if (existing) {
		trace(existing, 'reuse', { lingering: !!existing.closeTimeout });
		existing.closeTimeout = clearTimer(existing.closeTimeout);
		return existing;
	}

	const session = createSession(deviceId, streamId, key);
	sessions.set(key, session);
	trace(session, 'open');
	return session;
};

const replayState = (session: WebRTCSession, listeners: WebRTCSessionListeners) => {
	listeners.onLoading?.(session.loading);
	listeners.onError?.(session.error);
	listeners.onPttState?.(session.talk.state);
	listeners.onStream?.(session.stream);
};

const release = (session: WebRTCSession, listeners: WebRTCSessionListeners) => {
	if (!session.consumers.delete(listeners)) {
		return;
	}
	trace(session, 'release');

	if (session.talk.isClaimedBy(listeners)) {
		trace(session, 'talk consumer left, giving up the claim');
		session.talk.stop();
	}

	if (session.consumers.size) {
		return;
	}
	trace(session, `last consumer left, lingering ${SESSION_LINGER_MS}ms`);

	// The peer connection lingers so a screen switch can reuse it, but the
	// microphone is dropped at once so the OS indicator does not stay lit.
	session.talk.stop();
	session.closeTimeout = setTimeout(() => destroySession(session), SESSION_LINGER_MS);
};

/**
 * A handle can outlive its session — the browser-close teardown drops every
 * session while players are still mounted — so reconnecting must not revive an
 * orphan that nothing would ever tear down again.
 */
const retry = (session: WebRTCSession) => {
	if (sessions.get(session.key) !== session) {
		return;
	}
	session.retryTimeout = clearTimer(session.retryTimeout);
	setError(session, NO_STREAM_ERROR);
	connect(session);
};

/**
 * Leases the shared session for `deviceId`, negotiating one only if it is not
 * already open, and replays its current state to the new consumer.
 *
 * Every consumer must call `release` on the returned handle when it is done.
 */
export const acquireWebRTCSession = (
	deviceId: string,
	streamId: number,
	listeners: WebRTCSessionListeners,
): WebRTCSessionHandle => {
	registerUnloadTeardown();

	const session = openSession(deviceId, streamId);
	if (!session.client && !session.retryTimeout) {
		connect(session);
	}

	session.consumers.add(listeners);
	trace(session, 'acquire → replaying state', {
		loading: session.loading,
		ptt: session.talk.state,
		stream: session.stream ? 'live' : 'null',
	});
	replayState(session, listeners);

	return {
		getClient: () => session.client,
		requestTalk: (desired: boolean) => session.talk.request(listeners, desired),
		retry: () => retry(session),
		release: () => release(session, listeners),
	};
};

export const openWebRTCSession = (
	deviceId: string,
	streamId = 0,
	talkEvents: TalkConsumer = {},
): WebRTCSessionLease => {
	const state = writable({
		stream: null,
		loading: true,
		error: NO_STREAM_ERROR,
		pttState: PttUiState.UNAVAILABLE,
	});
	const handle = acquireWebRTCSession(deviceId, streamId, {
		...talkEvents,
		onStream: (stream) => state.update((current) => ({ ...current, stream })),
		onLoading: (loading) => state.update((current) => ({ ...current, loading })),
		onError: (error) => state.update((current) => ({ ...current, error })),
		onPttState: (pttState) => state.update((current) => ({ ...current, pttState })),
	});

	return {
		state,
		requestTalk: handle.requestTalk,
		retry: handle.retry,
		getClient: handle.getClient,
		release: handle.release,
	};
};
